import { Sparkles } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ICP_REQUEST_DRAFT_COST_RANGE_LABEL,
  IDEAL_CANDIDATE_PARTS,
  IDEAL_CANDIDATE_PROMPTS,
  idealProfileProgress,
  idealProfileSections,
  type IdealProfile,
  type JobDetail,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { useToasts } from '../../../../stores/toasts';
import { IdealProfileSteps } from '../../../hiring-templates/components/IdealProfileSteps';
import { EarlierText, WrittenBefore } from '../../../hiring-templates/components/TemplateSectionsView';
import { EditedMark } from '../../../requisitions/components/EditedMark';
import type { JevBudgetButton } from '../../../ranking/api/use-jev-budget-gate';
import { useToolNames } from '../../../tool-names/api/tool-names-api';
import { useSaveJobStep } from '../../api/jobs-api';
import { useStepAutosave } from '../../hooks/use-step-autosave';
import { useReportSaveState } from './builder-saves';
import styles from './JobIdealProfile.module.css';

/**
 * ADR 0092 (DECISIONS-LOG 2026-11-03): the job's Ideal candidate profile, the same eight steps as on Request a hire, filled with
 * what was copied from the request. HR edits and completes them here; they save as you type (F5.31). Below, Jev's rated lines stay
 * as they were, and "Turn into rated lines" drafts them from these saved boxes in one click (ADR 0075, 0083). A request written
 * before the template brings its four-part text, shown read-only above the steps.
 */

const SAVE_SOURCE = 'profile:boxes';

interface JobIdealProfileProps {
  readonly job: JobDetail;
  /** False for someone who may only read the section: the steps open, the boxes don't change. */
  readonly canEdit: boolean;
  readonly onConflict: (message: string) => void;
  readonly isDrafting: boolean;
  readonly onDraft: () => void;
  /** The interview's budget gate (ADR 0070 stage 2): priced by use, so it is off only when nothing is left. */
  readonly budget: JevBudgetButton;
}

export function JobIdealProfile({ job, canEdit, onConflict, isDrafting, onDraft, budget }: JobIdealProfileProps): ReactNode {
  // The boxes keep their own draft while HR types; a save landing mid-keystroke never puts back what was typed (as the brief).
  const [profile, setProfile] = useState<IdealProfile>(() => job.idealProfile);
  const saved = useRef(job.idealProfile);
  const saveStep = useSaveJobStep();
  const show = useToasts((toasts) => toasts.show);
  const toolNames = useToolNames();
  const { state, schedule, flush } = useStepAutosave<IdealProfile>({
    job,
    onConflict,
    save: async (next, version) => {
      const updated = await saveStep.mutateAsync({ id: job.id, step: 'ideal-profile', body: { version, idealProfile: next } });
      saved.current = next;
      return updated;
    },
  });
  useReportSaveState(SAVE_SOURCE, state, flush);

  // A refused save puts the last saved boxes back and says why (ui-instant-feel rule 1). A stale write reloads the section instead.
  useEffect(() => {
    if (state.kind !== 'error') return;
    setProfile(saved.current);
    show({ tone: 'danger', message: 'That change to the ideal candidate profile was not saved', detail: state.message });
  }, [state, show]);

  const note = job.idealCandidateNote;
  const parts = note === null ? [] : IDEAL_CANDIDATE_PARTS.filter((part) => note[part].trim() !== '');
  // A job made directly has no request to bring boxes from; it shows them only if it has some.
  const hasProfile = idealProfileSections(job.idealProfile, job.managesPeople).length > 0;
  if (job.requisitionId === null && parts.length === 0 && !hasProfile) return null;

  const progress = idealProfileProgress(profile, job.managesPeople);
  const source = job.requisitionId === null ? null : job.requestedByName ? `From ${job.requestedByName}’s hire request` : 'From the hire request';
  const done = progress.isComplete ? 'every required box filled' : `${progress.done} of ${progress.total} required boxes`;
  const isSaving = state.kind === 'pending' || state.kind === 'saving' || state.kind === 'error';
  const hasSomethingToDraft = parts.length > 0 || idealProfileSections(profile, job.managesPeople).length > 0;

  function change(next: IdealProfile): void {
    setProfile(next);
    schedule(next);
  }

  return (
    <section className={styles.card} aria-labelledby="job-ideal-profile-title">
      <div className={styles.head}>
        <div className={styles.headText}>
          <h3 id="job-ideal-profile-title" className={styles.title}>
            Ideal candidate profile
          </h3>
          <span className={styles.meta}>{source ? `${source} · ${done}` : done}</span>
        </div>
        <EditedMark mark={job.requestEditMarks.idealCandidate} />
      </div>
      {note !== null && parts.length > 0 && (
        <WrittenBefore title="Written before the template">
          {parts.map((part) => (
            <EarlierText key={part} text={`${IDEAL_CANDIDATE_PROMPTS[part].label}: ${note[part]}`} />
          ))}
        </WrittenBefore>
      )}
      <IdealProfileSteps value={profile} managesPeople={job.managesPeople} toolNames={toolNames} disabled={!canEdit} onChange={change} />
      {canEdit && (
        <div className={styles.foot}>
          <Button variant="solid" size="sm" icon={Sparkles} isPending={isDrafting} disabled={isSaving || !hasSomethingToDraft} isBlocked={Boolean(budget.blockedNote)} onClick={onDraft}>
            Turn into rated lines · about {ICP_REQUEST_DRAFT_COST_RANGE_LABEL}
            {budget.leftSuffix}
          </Button>
          <p className={styles.help} role={budget.blockedNote ? 'status' : undefined}>
            {budget.blockedNote ?? helpOf(isSaving, hasSomethingToDraft)}
          </p>
        </div>
      )}
    </section>
  );
}

function helpOf(isSaving: boolean, hasSomethingToDraft: boolean): string {
  if (!hasSomethingToDraft) return 'Fill in a box first, then the assistant can turn them into the lines below.';
  if (isSaving) return 'Saving your changes first.';
  return 'The assistant reads these boxes and the job’s own text, then fills the lines below. The lines aren’t saved until you save them.';
}
